import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CalendarCheck, Users, TrendingUp, Clock, Star, UserCheck, 
  ListOrdered, Loader2, BarChart3, DollarSign
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStatsSection = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('30d');
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());
  const [trendMonth, setTrendMonth] = useState(new Date().getMonth() + 1);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => { fetchStats(); }, []);

  const fetchTrend = useCallback(async (period, year, month) => {
    setTrendLoading(true);
    try {
      const params = new URLSearchParams({ period, year });
      if (period === 'month') params.set('month', month);
      const res = await axios.get(`${API}/admin/stats/trend?${params}`, { headers: getAuthHeaders() });
      setTrendData(res.data.data || []);
    } catch (error) {
      console.error('Errore trend:', error);
    } finally {
      setTrendLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
      setTrendData(res.data.daily_trend || []);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setTrendPeriod(newPeriod);
    if (newPeriod === '30d') {
      setTrendData(stats?.daily_trend || []);
    } else {
      fetchTrend(newPeriod, trendYear, trendMonth);
    }
  };

  const handleYearChange = (newYear) => {
    const y = parseInt(newYear);
    setTrendYear(y);
    if (trendPeriod !== '30d') fetchTrend(trendPeriod, y, trendMonth);
  };

  const handleMonthChange = (newMonth) => {
    const m = parseInt(newMonth);
    setTrendMonth(m);
    if (trendPeriod === 'month') fetchTrend('month', trendYear, m);
  };

  const formatTrendLabel = (dateStr) => {
    if (!dateStr) return '';
    if (trendPeriod === '6m' || trendPeriod === '1y') {
      const [y, m] = dateStr.split('-');
      return months[parseInt(m) - 1]?.substring(0, 3) || dateStr;
    }
    return format(new Date(dateStr), 'dd MMM', { locale: it });
  };

  const periodLabels = {
    '30d': 'Ultimi 30 Giorni',
    'month': `${months[trendMonth - 1]} ${trendYear}`,
    '6m': `Ultimi 6 Mesi`,
    '1y': `Anno ${trendYear}`
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!stats) {
    return <p className="text-center text-muted-foreground py-12">Impossibile caricare le statistiche</p>;
  }

  const statCards = [
    {
      icon: CalendarCheck,
      label: 'Appuntamenti Oggi',
      value: stats.today.total,
      sub: `${stats.today.confirmed} confermati, ${stats.today.pending} in attesa`,
      color: 'text-accent',
      bg: 'bg-accent/10',
    },
    {
      icon: TrendingUp,
      label: 'Questa Settimana',
      value: stats.week.total,
      sub: 'prenotazioni attive',
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10',
    },
    {
      icon: BarChart3,
      label: 'Questo Mese',
      value: stats.month.total,
      sub: 'prenotazioni attive',
      color: 'text-blue-400',
      bg: 'bg-blue-400/10',
    },
    {
      icon: DollarSign,
      label: 'Incasso Mese (stima)',
      value: `€${stats.month.revenue.toFixed(0)}`,
      sub: 'da prenotazioni confermate',
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10',
    },
    {
      icon: Users,
      label: 'Clienti Totali',
      value: stats.total_clients,
      sub: 'registrati nel sistema',
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
    },
    {
      icon: ListOrdered,
      label: "Lista d'Attesa",
      value: stats.waitlist_count,
      sub: 'in attesa di posto',
      color: 'text-orange-400',
      bg: 'bg-orange-400/10',
    },
  ];

  return (
    <div className="space-y-8" data-testid="stats-section">
      <h2 className="text-2xl sm:text-3xl font-bold">Statistiche</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stats-kpi-grid">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="card-elegant" data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-lg ${card.bg} flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">{card.label}</p>
                    <p className="text-2xl sm:text-3xl font-bold leading-none">{card.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Booking Trend Chart */}
      <Card className="card-elegant" data-testid="trend-chart-card">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-bold">Andamento Prenotazioni</h3>
            </div>

            {/* Period + Year + Month selectors */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="trend-controls">
              {/* Period tabs */}
              <div className="flex rounded-lg border border-border overflow-hidden" data-testid="period-selector">
                {[
                  { id: '30d', label: '30G' },
                  { id: 'month', label: 'Mese' },
                  { id: '6m', label: '6M' },
                  { id: '1y', label: 'Anno' },
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handlePeriodChange(p.id)}
                    data-testid={`period-${p.id}`}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      trendPeriod === p.id
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-secondary'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Year selector (visible for month, 6m, 1y) */}
              {trendPeriod !== '30d' && (
                <Select value={String(trendYear)} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-[90px] h-8 text-xs" data-testid="year-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Month selector (visible only for 'month' period) */}
              {trendPeriod === 'month' && (
                <Select value={String(trendMonth)} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="month-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-sm text-muted-foreground mb-4">{periodLabels[trendPeriod]}</p>

          {/* Chart */}
          <div className="h-[280px] w-full" data-testid="trend-chart">
            {trendLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData.map(d => ({
                  ...d,
                  label: formatTrendLabel(d.date)
                }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="accentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    interval={trendPeriod === '1y' || trendPeriod === '6m' ? 0 : Math.max(0, Math.floor(trendData.length / 7) - 1)}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'hsl(var(--foreground))',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                    formatter={(value) => [`${value} prenotazioni`, 'Totale']}
                    labelFormatter={(label) => label}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--accent))"
                    strokeWidth={2.5}
                    fill="url(#accentGradient)"
                    dot={trendPeriod === '1y' || trendPeriod === '6m'}
                    activeDot={{ r: 5, fill: 'hsl(var(--accent))', stroke: 'hsl(var(--background))', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout: Top Services + Top Operators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Services */}
        <Card className="card-elegant" data-testid="top-services-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-bold">Servizi Piu Richiesti</h3>
            </div>
            {stats.top_services.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nessun dato disponibile</p>
            ) : (
              <div className="space-y-3">
                {stats.top_services.map((service, i) => {
                  const maxCount = stats.top_services[0]?.count || 1;
                  const pct = Math.round((service.count / maxCount) * 100);
                  return (
                    <div key={service.name} data-testid={`top-service-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            i === 0 ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground'
                          }`}>{i + 1}</span>
                          {service.name}
                        </span>
                        <Badge variant="secondary" className="text-xs">{service.count} prenotazioni</Badge>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${pct}%`,
                            backgroundColor: i === 0 ? 'hsl(var(--accent))' : `hsl(var(--accent) / ${0.7 - i * 0.1})`
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Operators */}
        <Card className="card-elegant" data-testid="top-operators-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-bold">Operatori del Mese</h3>
            </div>
            {stats.top_operators.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nessun dato disponibile</p>
            ) : (
              <div className="space-y-3">
                {stats.top_operators.map((op, i) => {
                  const maxCount = stats.top_operators[0]?.count || 1;
                  const pct = Math.round((op.count / maxCount) * 100);
                  return (
                    <div key={op.name} data-testid={`top-operator-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center text-xs font-bold text-accent">
                            {op.name.charAt(0).toUpperCase()}
                          </span>
                          {op.name}
                        </span>
                        <Badge variant="secondary" className="text-xs">{op.count} appuntamenti</Badge>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Bookings Detail */}
      {stats.today.bookings.length > 0 && (
        <Card className="card-elegant" data-testid="today-bookings-detail">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-bold">Appuntamenti di Oggi</h3>
            </div>
            <div className="space-y-2">
              {stats.today.bookings
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                .map((b, i) => (
                  <div key={b.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50" data-testid={`today-booking-${i}`}>
                    <span className="text-sm font-bold text-accent w-12 flex-shrink-0">{b.time}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{b.client_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.service_name}{b.operator_name ? ` — ${b.operator_name}` : ''}</p>
                    </div>
                    <Badge className={
                      b.status === 'confirmed' ? 'bg-accent/20 text-accent' : 'bg-yellow-500/20 text-yellow-500'
                    }>
                      {b.status === 'confirmed' ? 'Confermato' : 'In Attesa'}
                    </Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Bookings */}
      {stats.recent_bookings?.length > 0 && (
        <Card className="card-elegant" data-testid="recent-bookings-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-bold">Ultime Prenotazioni</h3>
            </div>
            <div className="space-y-2">
              {stats.recent_bookings.map((b, i) => (
                <div key={b.id || i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50" data-testid={`recent-booking-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{b.client_name} — {b.service_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {b.date && format(new Date(b.date), 'dd MMM yyyy', { locale: it })} alle {b.time}
                      {b.operator_name ? ` — ${b.operator_name}` : ''}
                    </p>
                  </div>
                  <Badge className={
                    b.status === 'confirmed' ? 'bg-accent/20 text-accent'
                    : b.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500'
                    : 'bg-red-500/20 text-red-500'
                  }>
                    {b.status === 'confirmed' ? 'Confermato' : b.status === 'pending' ? 'In Attesa' : 'Annullato'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminStatsSection;
